import React, { useState, useEffect } from 'react';
import { Button, Alert, Modal } from 'react-bootstrap';
import {
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Plus,
  Reply,
  Edit3,
  Trash2,
  Send,
  X,
  ImageIcon,
  Upload,
  Check,
  User
} from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import Swal from 'sweetalert2';
import 'react-toastify/dist/ReactToastify.css';
import { 
  getCommentsByEntity, 
  createComment, 
  updateComment, 
  deleteComment, 
  getCurrentUser,
  uploadFile,
  getUploadedFiles,
  deleteUploadedImage // NEW: Import the delete function
} from '../../../services/CommentsServices.js';
import { placementUrl } from '../../../services/AxiosInstance.js';

const CommentsSection = ({ 
  entityId,                    // commentOnId - the ID of the entity (placement, contact, etc.)
  type = 'jobprofiles',        // type - jobprofiles, hrcontacts, organizations, etc.
  title = 'Comments & Discussion',
  users = [],
  className = '',
  style = {},
  onError = null,
  showCollapse = true,
  defaultExpanded = true,
  maxHeight = '500px'
}) => {
  // Comments state
  const [comments, setComments] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [editingComment, setEditingComment] = useState(null);
  const [editText, setEditText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showComments, setShowComments] = useState(defaultExpanded);
  const [isLoading, setIsLoading] = useState(true);

  // Image attachment states
  const [showImageModal, setShowImageModal] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [activeAttachmentFor, setActiveAttachmentFor] = useState(null);
  const [selectedImageForEdit, setSelectedImageForEdit] = useState('');

  // Image preview modal states
  const [showImagePreviewModal, setShowImagePreviewModal] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');

  // NEW: Image deletion state
  const [isDeletingImage, setIsDeletingImage] = useState(false);

  // FIXED: Helper function to construct proper image URLs
  const getImageSrc = (imagePath, baseUrl = null) => {
    if (!imagePath) return null;
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    
    // Construct base URL
    const apiBaseUrl = baseUrl || placementUrl;
    
    // If it starts with /, it's a relative path from server root
    if (imagePath.startsWith('/')) {
      // Properly encode the URL to handle spaces and special characters
      const encodedPath = imagePath.split('/').map(segment => 
        segment ? encodeURIComponent(segment) : segment
      ).join('/');
      
      return `${apiBaseUrl}${encodedPath}`;
    }
    
    // Fallback: encode the entire path
    return `${apiBaseUrl}/${encodeURIComponent(imagePath)}`;
  };

  // Enhanced error handling for images
  const handleImageError = (e, imagePath) => {
    console.error('❌ Image failed to load:', e.target.src);
    // console.log('📁 Original path:', imagePath);
    
    // Try alternative URL construction methods
    const alternatives = [
      imagePath, // Original
      imagePath?.replace(/\s+/g, '%20'), // Manual space encoding
      imagePath?.replace(/\s+/g, '_'), // Replace spaces with underscores
      // `http://localhost:3030${imagePath}` // Direct construction
    ];
    
    // console.log('🔄 Alternative URLs to try:', alternatives);
    
    // Hide broken image gracefully
    e.target.style.display = 'none';
    
    // Show placeholder
    const parent = e.target.parentElement;
    if (parent && !parent.querySelector('.image-error-placeholder')) {
      const placeholder = document.createElement('div');
      placeholder.className = 'image-error-placeholder';
      placeholder.innerHTML = `
        <div style="
          padding: 20px; 
          background: #f3f4f6; 
          border-radius: 8px; 
          text-align: center; 
          color: #9ca3af; 
          font-size: 0.8rem;
          border: 2px dashed #e5e7eb;
        ">
          <div style="margin-bottom: 8px;">📷</div>
          <div>Image not available</div>
          <small style="font-size: 0.7rem; opacity: 0.7;">Check server connectivity</small>
        </div>
      `;
      parent.appendChild(placeholder);
    }
  };

  const handleImageLoad = (e) => {
    // console.log('✅ Image loaded successfully:', e.target.src);
    // Remove any error placeholders
    const parent = e.target.parentElement;
    if (parent) {
      const placeholder = parent.querySelector('.image-error-placeholder');
      if (placeholder) {
        placeholder.remove();
      }
    }
  };

  // NEW: Handle image deletion with confirmation
  const handleDeleteImage = async (image) => {
    if (!currentUser || isDeletingImage) return;

    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: `You want to delete "${image.displayName || image.fileName || 'this image'}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        focusCancel: true
      });

      if (result.isConfirmed) {
        setIsDeletingImage(true);
        
        // Use the deleteUploadedImage function from services
        const deleteResult = await deleteUploadedImage(
          image.id, 
          image.path || image.filePath
        );
        
        if (deleteResult.success) {
          // Refresh the images list
          await loadUploadedImages();
          
          // Clear selected image if it was the deleted one
          if (selectedImage === (image.path || image.filePath)) {
            setSelectedImage('');
          }
          if (selectedImageForEdit === (image.path || image.filePath)) {
            setSelectedImageForEdit('');
          }
          
          toast.success(`Image "${image.displayName || image.fileName || 'Image'}" deleted successfully!`);
          
          Swal.fire({
            title: 'Deleted!',
            text: 'Your image has been deleted.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });
        }
      }
    } catch (error) {
      console.error('Failed to delete image:', error);
      
      let errorMessage = 'Failed to delete image. Please try again.';
      
      // Handle specific error cases
      if (error.message.includes('cannot be deleted because')) {
        errorMessage = error.message;
      } else if (error.message.includes('do not have permission')) {
        errorMessage = 'You do not have permission to delete this image.';
      }
      
      toast.error(errorMessage);
      
      Swal.fire({
        title: 'Error!',
        text: errorMessage,
        icon: 'error',
        confirmButtonColor: '#6366f1'
      });
    } finally {
      setIsDeletingImage(false);
    }
  };

  // Use ref to prevent duplicate data fetching for the same entity
  const lastLoadedKey = React.useRef(null);

  // Load data on component mount or when entityId/type changes
  useEffect(() => {
    const currentKey = `${entityId}_${type}`;
    if (entityId && currentKey !== lastLoadedKey.current) {
      loadAllData();
      lastLoadedKey.current = currentKey;
    }
  }, [entityId, type]);

  const loadAllData = async () => {
    try {
      setIsLoading(true);
      await Promise.all([
        loadCurrentUser(),
        loadComments(),
        loadUploadedImages()
      ]);
    } catch (error) {
      console.error('Failed to load comments data:', error);
      const errorMessage = 'Failed to load comments. Please refresh and try again.';
      setError(errorMessage);
      toast.error(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const loadCurrentUser = async () => {
    try {
      const response = await getCurrentUser();
      if (response && response.data) {
        setCurrentUser(response.data);
      }
    } catch (error) {
      console.error('Failed to load current user:', error);
      toast.error('Failed to load user information');
    }
  };

  const loadComments = async () => {
    try {
      if (!entityId) {
        // console.log('⚠️ No entityId provided');
        return;
      }
      
      // console.log('🔄 Loading comments for entityId:', entityId, 'type:', type);
      
      const response = await getCommentsByEntity(entityId, type);
      
      // console.log('📥 Comments response:', response);
      
      if (response && response.data) {
        // Handle both direct array and nested data structure
        const commentsData = Array.isArray(response.data) ? response.data : response.data.data || [];
        
        // console.log('📋 Comments data:', commentsData);
        
        // Additional filtering for safety (already done in service, but double-check)
        const filteredComments = commentsData.filter(comment => {
          const entityIdFromComment = Array.isArray(comment.commentOnId) 
            ? comment.commentOnId[0]?.id 
            : (typeof comment.commentOnId === 'object' ? comment.commentOnId.id : comment.commentOnId);
            
          return comment.status === 'active' && 
                 entityIdFromComment === entityId &&
                 !comment.isDeleted;
        });
        
        // console.log('✅ Final filtered comments:', filteredComments);
        
        setComments(filteredComments);
      } else {
        // console.log('⚠️ No data in response');
        setComments([]);
      }
    } catch (error) {
      console.error('❌ Failed to load comments:', error);
      const errorMessage = 'Failed to load comments.';
      setError(errorMessage);
      toast.error(errorMessage);
      if (onError) onError(errorMessage);
    }
  };

  const loadUploadedImages = async () => {
    try {
      const response = await getUploadedFiles();
      // console.log('🖼️ Uploaded images response:', response);
      
      // Handle the updated response format from the fixed service
      if (Array.isArray(response)) {
        setUploadedImages(response);
      } else if (response && response.data) {
        setUploadedImages(response.data);
      } else {
        setUploadedImages([]);
      }
      
      // Debug: Log image URLs
      const images = Array.isArray(response) ? response : (response?.data || []);
      images.forEach((image, index) => {
        const imageSrc = getImageSrc(image.path || image.filePath);
        // console.log(`🖼️ Image ${index + 1}:`, {
        //   original: image.path || image.filePath,
        //   constructed: imageSrc,
        //   fileName: image.fileName
        // });
      });
      
    } catch (error) {
      console.error('Failed to load uploaded images:', error);
      toast.warning('Failed to load image gallery');
    }
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file.');
      return;
    }

    // Check file size (limit to 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size should be less than 10MB.');
      return;
    }

    try {
      setIsUploading(true);
      toast.info(`Uploading "${file.name}"...`);
      
      const response = await uploadFile(file);
      // console.log('📤 Upload response:', response);
      
      if (response && (response.path || response.filePath)) {
        await loadUploadedImages();
        const imagePath = response.path || response.filePath;
        setSelectedImage(imagePath);
        toast.success(`Image "${response.originalFileName || file.name}" uploaded successfully!`);
      }
    } catch (error) {
      console.error('Failed to upload file:', error);
      toast.error(error.message || 'Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // Open image selection modal
  const openImageModal = (modalType, commentId = null) => {
    setActiveAttachmentFor(modalType === 'comment' ? 'comment' : commentId);
    setShowImageModal(true);
    if (modalType === 'edit' && commentId) {
      const comment = comments.find(c => c.id === commentId);
      setSelectedImageForEdit(comment?.attachmentsPath || '');
    }
  };

  // Select image from gallery
  const selectImageFromGallery = (imagePath) => {
    if (activeAttachmentFor === 'comment') {
      setSelectedImage(imagePath);
    } else if (editingComment) {
      setSelectedImageForEdit(imagePath);
    } else {
      setSelectedImage(imagePath);
    }
    setShowImageModal(false);
    toast.info('Image selected successfully!');
  };

  const addComment = async () => {
    if (!newComment.trim() || !currentUser || isSubmitting) return;

    try {
      setIsSubmitting(true);
      setError(null);
      
      const payload = {
        commentorId: currentUser.id,
        commentOnId: entityId,              // Use entityId as commentOnId
        comment: newComment.trim(),
        attachmentsPath: selectedImage || '',
        status: 'active',
        priority: 'medium',
        type: type,                         // Use type passed from parent component
        repliedTo: '',
        createdById: currentUser.id,
        updatedById: currentUser.id
      };

      // console.log('📝 Creating comment with payload:', payload);

      const response = await createComment(payload);
      if (response) {
        setNewComment('');
        setSelectedImage('');
        await loadComments();
        toast.success('Comment added successfully!');
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addReply = async (parentCommentId) => {
    if (!replyText.trim() || !currentUser || isSubmitting) return;

    try {
      setIsSubmitting(true);
      setError(null);
      
      const payload = {
        commentorId: currentUser.id,
        commentOnId: entityId,              // Use entityId as commentOnId
        comment: replyText.trim(),
        attachmentsPath: selectedImage || '',
        status: 'active',
        priority: 'medium',
        type: type,                         // Use type passed from parent component
        repliedTo: parentCommentId,
        createdById: currentUser.id,
        updatedById: currentUser.id
      };

      const response = await createComment(payload);
      if (response) {
        setReplyText('');
        setSelectedImage('');
        setReplyingTo(null);
        await loadComments();
        toast.success('Reply added successfully!');
      }
    } catch (error) {
      console.error('Failed to add reply:', error);
      toast.error('Failed to add reply. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateCommentHandler = async (commentId) => {
    if (!editText.trim() || !currentUser || isSubmitting) return;

    try {
      setIsSubmitting(true);
      setError(null);
      
      const payload = {
        comment: editText.trim(),
        attachmentsPath: selectedImageForEdit || '',
        type: type,                         // Use type passed from parent component
        updatedById: currentUser.id
      };

      const response = await updateComment(commentId, payload);
      if (response) {
        setEditText('');
        setSelectedImageForEdit('');
        setEditingComment(null);
        await loadComments();
        toast.success('Comment updated successfully!');
      }
    } catch (error) {
      console.error('Failed to update comment:', error);
      toast.error('Failed to update comment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // SweetAlert2 confirmation for delete
  const deleteCommentHandler = async (commentId) => {
    if (!currentUser || isSubmitting) return;

    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this action!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        focusCancel: true
      });

      if (result.isConfirmed) {
        setIsSubmitting(true);
        setError(null);
        
        await deleteComment(commentId);
        await loadComments();
        
        toast.success('Comment deleted successfully!');
        
        Swal.fire({
          title: 'Deleted!',
          text: 'Your comment has been deleted.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Failed to delete comment:', error);
      toast.error('Failed to delete comment. Please try again.');
      
      Swal.fire({
        title: 'Error!',
        text: 'Failed to delete comment. Please try again.',
        icon: 'error',
        confirmButtonColor: '#6366f1'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get commenter name by commentorId
  const getCommenterName = (commentorId) => {
    if (!commentorId) return 'Unknown User';
    
    // Handle new array-based structure from API
    if (Array.isArray(commentorId) && commentorId.length > 0) {
      const userObj = commentorId[0];
      if (userObj.firstName || userObj.lastName) {
        return `${userObj.firstName || ''} ${userObj.lastName || ''}`.trim() || 'Unknown User';
      }
      // If it's just an array of IDs
      commentorId = userObj.id || userObj;
    }
    
    // Handle object structure
    if (typeof commentorId === 'object' && commentorId !== null) {
      if (commentorId.firstName || commentorId.lastName) {
        return `${commentorId.firstName || ''} ${commentorId.lastName || ''}`.trim();
      }
      commentorId = commentorId.id || commentorId._id;
    }
    
    const user = users.find(u => u.id === commentorId);
    if (user) {
      return `${user.firstName} ${user.lastName}`;
    }
    
    if (currentUser && (currentUser.id === commentorId || currentUser._id === commentorId)) {
      return `${currentUser.firstName} ${currentUser.lastName}`;
    }
    
    return 'Unknown User';
  };

  // Get commenter initials
  const getCommenterInitials = (commentorId) => {
    if (!commentorId) return 'UN';
    
    // Handle new array-based structure from API
    if (Array.isArray(commentorId) && commentorId.length > 0) {
      const userObj = commentorId[0];
      if (userObj.firstName || userObj.lastName) {
        return `${userObj.firstName?.charAt(0) || ''}${userObj.lastName?.charAt(0) || ''}`.toUpperCase() || 'UN';
      }
      commentorId = userObj.id || userObj;
    }

    // Handle object structure
    if (typeof commentorId === 'object' && commentorId !== null) {
      if (commentorId.firstName || commentorId.lastName) {
        return `${commentorId.firstName?.charAt(0) || ''}${commentorId.lastName?.charAt(0) || ''}`.toUpperCase();
      }
      commentorId = commentorId.id || commentorId._id;
    }
    
    const user = users.find(u => u.id === commentorId);
    if (user) {
      return `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`.toUpperCase();
    }
    
    if (currentUser && (currentUser.id === commentorId || currentUser._id === commentorId)) {
      return `${currentUser.firstName?.charAt(0) || ''}${currentUser.lastName?.charAt(0) || ''}`.toUpperCase();
    }
    
    return 'UN';
  };

  // Organize comments into hierarchical structure
  const organizeComments = () => {
    const commentMap = {};
    const rootComments = [];

    comments.forEach(comment => {
      commentMap[comment.id] = {
        ...comment,
        replies: []
      };
    });

    comments.forEach(comment => {
      // Handle repliedTo being an array of objects or a scalar
      const parentId = Array.isArray(comment.repliedTo) 
        ? comment.repliedTo[0]?.id 
        : (typeof comment.repliedTo === 'object' && comment.repliedTo !== null ? (comment.repliedTo.id || comment.repliedTo._id) : comment.repliedTo);

      if (!parentId || parentId === "" || parentId === "0") {
        rootComments.push(commentMap[comment.id]);
      } else {
        if (commentMap[parentId]) {
          commentMap[parentId].replies.push(commentMap[comment.id]);
        } else {
          // If parent not found in current set (maybe it's a deleted parent), treat as root
          rootComments.push(commentMap[comment.id]);
        }
      }
    });

    const sortComments = (commentsList) => {
      return commentsList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    };

    rootComments.forEach(comment => {
      comment.replies = sortComments(comment.replies);
    });

    return sortComments(rootComments);
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'now';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Handle Enter key for inputs
  const handleKeyDown = (e, action, ...args) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      action(...args);
    }
  };

  // FIXED: Render attachment preview with proper URL handling
  const renderAttachment = (attachmentsPath) => {
    if (!attachmentsPath) return null;
    
    const imageSrc = getImageSrc(attachmentsPath);
    if (!imageSrc) return null;
    
    return (
      <div className="comment-attachment">
        <img 
          src={imageSrc}
          alt="Attachment" 
          className="attachment-image"
          onError={(e) => handleImageError(e, attachmentsPath)}
          onLoad={handleImageLoad}
          onClick={() => {
            setPreviewImageUrl(imageSrc);
            setShowImagePreviewModal(true);
          }}
          style={{
            cursor: 'pointer',
            transition: 'transform 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
        />
      </div>
    );
  };

  // Render comment component recursively
  const renderComment = (comment, depth = 0) => {
    const commentorIdScalar = Array.isArray(comment.commentorId) 
      ? comment.commentorId[0]?.id 
      : (typeof comment.commentorId === 'object' ? (comment.commentorId.id || comment.commentorId._id) : comment.commentorId);

    const isOwner = currentUser && (
      currentUser.id === commentorIdScalar || 
      currentUser._id === commentorIdScalar ||
      currentUser.isSuperAdmin === true || 
      currentUser.isSuperAdmin === 'true'
    );
    const maxDepth = 3;

    return (
      <div key={comment.id} className={`comment-thread ${depth > 0 ? 'reply-thread' : ''}`}>
        <div className="comment-item">
          <div className="comment-avatar">
            {getCommenterInitials(comment.commentorId)}
          </div>
          
          <div className="comment-content">
            <div className="comment-header">
              <span className="comment-author">{getCommenterName(comment.commentorId)}</span>
              <span className="comment-time">{formatDate(comment.createdAt)}</span>
              {comment.createdAt !== comment.updatedAt && (
                <span className="comment-edited">edited</span>
              )}
            </div>

            <div className="comment-body">
              {editingComment === comment.id ? (
                <div className="comment-edit-form">
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="comment-edit-input"
                    disabled={isSubmitting}
                    rows="3"
                    onKeyDown={(e) => handleKeyDown(e, updateCommentHandler, comment.id)}
                  />
                  
                  <div className="attachment-section">
                    <button
                      type="button"
                      onClick={() => openImageModal('edit', comment.id)}
                      className="attachment-btn"
                      disabled={isSubmitting}
                    >
                      <ImageIcon size={14} />
                      {selectedImageForEdit ? 'Change Image' : 'Add Image'}
                    </button>
                    
                    {selectedImageForEdit && (
                      <div className="selected-attachment">
                        <img 
                          src={getImageSrc(selectedImageForEdit)} 
                          alt="Selected" 
                          className="attachment-preview"
                          onError={(e) => handleImageError(e, selectedImageForEdit)}
                          onLoad={handleImageLoad}
                        />
                        <button
                          type="button"
                          onClick={() => setSelectedImageForEdit('')}
                          className="remove-attachment"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="comment-edit-actions">
                    <button
                      onClick={() => updateCommentHandler(comment.id)}
                      disabled={!editText.trim() || isSubmitting}
                      className="btn-save"
                    >
                      {isSubmitting ? (
                        <div className="spinner-inline" />
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setEditingComment(null);
                        setEditText('');
                        setSelectedImageForEdit('');
                      }}
                      disabled={isSubmitting}
                      className="btn-cancel"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="comment-text">{comment.comment}</p>
                  {renderAttachment(comment.attachmentsPath)}
                </>
              )}
            </div>

            <div className="comment-actions">
              {depth < maxDepth && currentUser && (
                <button
                  onClick={() => {
                    setReplyingTo(replyingTo === comment.id ? null : comment.id);
                    setReplyText('');
                    setSelectedImage('');
                  }}
                  className="comment-action-btn"
                  disabled={isSubmitting}
                >
                  <Reply size={12} />
                  Reply
                </button>
              )}
              
              {isOwner && (
                <>
                  <button
                    onClick={() => {
                      setEditingComment(comment.id);
                      setEditText(comment.comment);
                      setSelectedImageForEdit(comment.attachmentsPath || '');
                    }}
                    className="comment-action-btn"
                    disabled={isSubmitting}
                  >
                    <Edit3 size={12} />
                    Edit
                  </button>
                  <button
                    onClick={() => deleteCommentHandler(comment.id)}
                    className="comment-action-btn delete"
                    disabled={isSubmitting}
                  >
                    <Trash2 size={12} />
                    Delete
                  </button>
                </>
              )}
              
              {comment.replies.length > 0 && (
                <span className="replies-count">
                  {comment.replies.length} repl{comment.replies.length === 1 ? 'y' : 'ies'}
                </span>
              )}
            </div>

            {replyingTo === comment.id && (
              <div className="reply-form">
                <div className="reply-input-container">
                  <div className="reply-avatar">
                    {currentUser ? 
                      `${currentUser.firstName?.charAt(0) || ''}${currentUser.lastName?.charAt(0) || ''}`.toUpperCase() :
                      'UN'
                    }
                  </div>
                  <div className="reply-input-wrapper">
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Write a reply..."
                      className="reply-input"
                      disabled={isSubmitting}
                      rows={3}
                      onKeyDown={(e) => handleKeyDown(e, addReply, comment.id)}
                    />
                    
                    <div className="attachment-section">
                      <button
                        type="button"
                        onClick={() => openImageModal('reply', comment.id)}
                        className="attachment-btn"
                        disabled={isSubmitting}
                      >
                        <ImageIcon size={14} />
                        Add Image
                      </button>
                      
                      {selectedImage && replyingTo === comment.id && (
                        <div className="selected-attachment">
                          <img 
                            src={getImageSrc(selectedImage)} 
                            alt="Selected" 
                            className="attachment-preview"
                            onError={(e) => handleImageError(e, selectedImage)}
                            onLoad={handleImageLoad}
                          />
                          <button
                            type="button"
                            onClick={() => setSelectedImage('')}
                            className="remove-attachment"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="reply-actions">
                  <button
                    onClick={() => addReply(comment.id)}
                    disabled={!replyText.trim() || isSubmitting}
                    className="btn-reply"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="spinner-inline" />
                        Posting...
                      </>
                    ) : (
                      <>
                        <Send size={14} />
                        Post Reply
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setReplyingTo(null);
                      setReplyText('');
                      setSelectedImage('');
                    }}
                    disabled={isSubmitting}
                    className="btn-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {comment.replies && comment.replies.length > 0 && (
          <div className="replies-container">
            {comment.replies.map(reply => renderComment(reply, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (!entityId) {
    return (
      <div className="comments-section-error">
        <Alert variant="warning">
          Entity ID is required to load comments.
        </Alert>
      </div>
    );
  }

  const organizedComments = organizeComments();

  return (
    <>
      <div className={`comments-section-compact ${className}`} style={style}>
        <div className="comments-header-compact">
          <div className="comments-title">
            <MessageSquare size={18} />
            <span>{title}</span>
            <span className="comments-count">{comments.length}</span>
          </div>
          {showCollapse && (
            <button 
              onClick={() => setShowComments(!showComments)}
              className="toggle-comments-btn"
            >
              {showComments ? (
                <>
                  <ChevronUp size={14} />
                  Collapse
                </>
              ) : (
                <>
                  <ChevronDown size={14} />
                  Expand
                </>
              )}
            </button>
          )}
        </div>
        
        {/* Fixed Error Display with Proper Close Button */}
        {error && (
          <div className="comments-error">
            <Alert variant="danger" dismissible onClose={() => setError(null)} className="custom-alert">
              <div className="alert-content">
                <span>{error}</span>
              </div>
            </Alert>
          </div>
        )}

        {/* Add New Comment */}
        {currentUser && (
          <div className="add-comment-compact">
            <div className="comment-input-group">
              <div className="input-avatar">
                {currentUser.firstName && currentUser.lastName ? 
                  `${currentUser.firstName.charAt(0)}${currentUser.lastName.charAt(0)}`.toUpperCase() :
                  'UN'
                }
              </div>
              <div className="comment-input-wrapper">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={`Share your thoughts about this ${type.replace('profiles', '')}...`}
                  className="comment-input-compact"
                  disabled={isSubmitting}
                  rows="3"
                  onKeyDown={(e) => handleKeyDown(e, addComment)}
                />
                
                <div className="attachment-section">
                  <button
                    type="button"
                    onClick={() => openImageModal('comment')}
                    className="attachment-btn"
                    disabled={isSubmitting}
                  >
                    <ImageIcon size={14} />
                    Add Image
                  </button>
                  
                  {selectedImage && activeAttachmentFor === 'comment' && (
                    <div className="selected-attachment">
                      <img 
                        src={getImageSrc(selectedImage)} 
                        alt="Selected" 
                        className="attachment-preview"
                        onError={(e) => handleImageError(e, selectedImage)}
                        onLoad={handleImageLoad}
                      />
                      <button
                        type="button"
                        onClick={() => setSelectedImage('')}
                        className="remove-attachment"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="add-comment-actions">
              <div className="comment-help-text">
                Press Enter to post • Shift+Enter for new line
              </div>
              <button
                onClick={addComment}
                disabled={!newComment.trim() || isSubmitting}
                className="add-comment-btn"
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner-inline" />
                    Posting...
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    Add Comment
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {showComments && (
          <>
            {isLoading ? (
              <div className="comments-loading">
                <div className="text-center py-4">
                  <div className="spinner-inline" />
                  <span className="ms-2">Loading comments...</span>
                </div>
              </div>
            ) : (
              <div className="comments-list-compact" style={{ maxHeight }}>
                {organizedComments.length > 0 ? (
                  organizedComments.map(comment => renderComment(comment))
                ) : (
                  <div className="no-comments">
                    <MessageSquare size={32} />
                    <span>No comments yet. Start the conversation!</span>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Image Selection Modal with DELETE functionality */}
      <Modal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        size="lg"
        centered
        className="image-selection-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Select Image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="upload-section">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              id="imageUpload"
              disabled={isUploading}
            />
            <label
              htmlFor="imageUpload"
              className={`upload-btn ${isUploading ? 'uploading' : ''}`}
            >
              {isUploading ? (
                <>
                  <div className="spinner-inline" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Upload New Image
                </>
              )}
            </label>
          </div>

          <div className="image-gallery">
            {uploadedImages.length > 0 ? (
              uploadedImages.map((image, index) => {
                const imagePath = image.path || image.filePath;
                const imageSrc = getImageSrc(imagePath);
                const displayName = image.displayName || image.fileName || `Image ${index + 1}`;
                
                // Check if this is user's own image or if user is admin
                const canDelete = currentUser && (
                  currentUser.isSuperAdmin || 
                  image.createdBy === currentUser.id || 
                  image.updatedBy === currentUser.id
                );
                
                return (
                  <div
                    key={image.id || index}
                    className={`image-item ${selectedImage === imagePath ? 'selected' : ''}`}
                    title={displayName}
                  >
                    <img 
                      src={imageSrc} 
                      alt={displayName}
                      onError={(e) => handleImageError(e, imagePath)}
                      onLoad={handleImageLoad}
                      onClick={() => selectImageFromGallery(imagePath)}
                    />
                    
                    {selectedImage === imagePath && (
                      <div className="image-selected-indicator">
                        <Check size={16} />
                      </div>
                    )}
                    
                    {/* NEW: Delete button for images */}
                    {canDelete && (
                      <button
                        className="image-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent image selection
                          handleDeleteImage(image);
                        }}
                        disabled={isDeletingImage}
                        title="Delete image"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                    
                    {/* Image filename overlay */}
                    <div className="image-overlay">
                      <span className="image-filename">
                        {displayName}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-images">
                <ImageIcon size={48} />
                <p>No images uploaded yet. Upload your first image!</p>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImageModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={() => setShowImageModal(false)}
            disabled={!selectedImage}
          >
            Select Image
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Image Preview Modal with FIXED image display */}
      <Modal
        show={showImagePreviewModal}
        onHide={() => setShowImagePreviewModal(false)}
        size="lg"
        centered
        className="image-preview-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <img 
            src={previewImageUrl} 
            alt="Preview" 
            style={{ 
              maxWidth: '100%', 
              maxHeight: '70vh', 
              objectFit: 'contain',
              borderRadius: '8px'
            }}
            onError={(e) => {
              console.error('Preview image failed to load:', e.target.src);
              e.target.style.display = 'none';
              // Show error message in preview
              const errorDiv = document.createElement('div');
              errorDiv.innerHTML = `
                <div style="
                  padding: 40px; 
                  color: #9ca3af; 
                  text-align: center;
                  font-size: 1rem;
                ">
                  <div style="font-size: 3rem; margin-bottom: 16px;">📷</div>
                  <div>Unable to load image preview</div>
                  <small style="font-size: 0.7rem; opacity: 0.7;">Please check the image URL or try uploading again</small>
                </div>
              `;
              e.target.parentElement.appendChild(errorDiv);
            }}
            // onLoad={() => console.log('Preview image loaded successfully')}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowImagePreviewModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Toast Container - Positioned at top right */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        toastClassName="custom-toast"
        bodyClassName="custom-toast-body"
        progressClassName="custom-toast-progress"
      />

      <style>{`
        /* All existing styles plus new DELETE button styles */
        .comments-section-compact {
          margin-top: 24px;
          border: 1px solid #e9ecef;
          border-radius: 12px;
          background: white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
        }

        .comments-header-compact {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid #f1f3f4;
          background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
          border-radius: 12px 12px 0 0;
        }

        .comments-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-weight: 600;
          font-size: 1rem;
          color: #374151;
        }

        .comments-count {
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          color: white;
          padding: 4px 10px;
          border-radius: 16px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .toggle-comments-btn {
          background: none;
          border: 1px solid #e5e7eb;
          color: #6366f1;
          font-size: 0.85rem;
          font-weight: 500;
          cursor: pointer;
          padding: 6px 12px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          gap: 4px;
          transition: all 0.2s ease;
        }

        .toggle-comments-btn:hover {
          background: #f3f4f6;
          border-color: #6366f1;
        }

        /* Fixed Error Alert Styles */
        .comments-error {
          padding: 12px 20px 0;
          border-bottom: 1px solid #f1f3f4;
        }

        .custom-alert {
          margin-bottom: 12px;
          border: 1px solid #f5c6cb;
          border-radius: 8px;
          background-color: #f8d7da;
          padding: 12px 16px;
          position: relative;
        }

        .custom-alert .alert-content {
          padding-right: 30px;
          color: #721c24;
          font-size: 0.9rem;
        }

        .custom-alert .btn-close {
          position: absolute;
          top: 50%;
          right: 12px;
          transform: translateY(-50%);
          background: none;
          border: none;
          font-size: 1.2rem;
          color: #721c24;
          opacity: 0.7;
          cursor: pointer;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .custom-alert .btn-close:hover {
          opacity: 1;
        }

        /* Continue with all other existing styles... */
        .add-comment-compact {
          padding: 20px;
          border-bottom: 1px solid #f1f3f4;
          background: #fafbfc;
        }

        .comment-input-group {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 12px;
        }

        .comment-input-wrapper {
          flex: 1;
        }

        .add-comment-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .input-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 0.8rem;
          flex-shrink: 0;
        }

        .comment-input-compact {
          width: 100%;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          padding: 12px 16px;
          font-size: 0.9rem;
          outline: none;
          resize: vertical;
          min-height: 80px;
          transition: all 0.2s ease;
          font-family: inherit;
        }

        .comment-input-compact:focus {
          border-color: #6366f1;
          box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
        }

        .add-comment-btn {
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          border: none;
          color: white;
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s ease;
          box-shadow: 0 2px 4px rgba(99, 102, 241, 0.2);
        }

        .add-comment-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #5b5bd6 0%, #7c3aed 100%);
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(99, 102, 241, 0.3);
        }

        .add-comment-btn:disabled {
          background: #d1d5db;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .comment-help-text {
          font-size: 0.75rem;
          color: #9ca3af;
        }

        .comments-loading {
          padding: 20px;
          text-align: center;
          color: #6b7280;
        }

        .comments-list-compact {
          overflow-y: auto;
          padding: 16px 0;
        }

        .comment-thread {
          padding: 12px 20px;
          border-bottom: 1px solid #f9fafb;
          transition: background-color 0.2s ease;
        }

        .comment-thread:hover {
          background: #fafbfc;
        }

        .comment-thread:last-child {
          border-bottom: none;
        }

        .reply-thread {
          margin-left: 24px;
          border-left: 3px solid #e5e7eb;
          padding-left: 20px;
          background: linear-gradient(90deg, #f9fafb 0%, transparent 100%);
        }

        .comment-item {
          display: flex;
          gap: 12px;
        }

        .comment-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 0.7rem;
          flex-shrink: 0;
        }

        .comment-content {
          flex: 1;
          min-width: 0;
        }

        .comment-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 6px;
        }

        .comment-author {
          font-weight: 600;
          font-size: 0.85rem;
          color: #374151;
        }

        .comment-time {
          font-size: 0.75rem;
          color: #9ca3af;
        }

        .comment-edited {
          font-size: 0.7rem;
          color: #9ca3af;
          font-style: italic;
        }

        .comment-body {
          margin-bottom: 8px;
        }

        .comment-text {
          font-size: 0.9rem;
          line-height: 1.5;
          color: #374151;
          margin: 0;
        }

        .comment-actions {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-top: 6px;
        }

        .comment-action-btn {
          background: none;
          border: none;
          color: #6b7280;
          font-size: 0.8rem;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s ease;
          font-weight: 500;
        }

        .comment-action-btn:hover {
          color: #374151;
          background: #f3f4f6;
        }

        .comment-action-btn.delete:hover {
          color: #dc2626;
          background: #fef2f2;
        }

        .replies-count {
          font-size: 0.75rem;
          color: #9ca3af;
          margin-left: auto;
          font-weight: 500;
        }

        .reply-form {
          margin-top: 12px;
          padding: 16px;
          background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
          border-radius: 12px;
          border: 1px solid #e5e7eb;
        }

        .reply-input-container {
          display: flex;
          gap: 10px;
          margin-bottom: 10px;
        }

        .reply-input-wrapper {
          flex: 1;
        }

        .reply-avatar {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 0.65rem;
          flex-shrink: 0;
        }

        .reply-input {
          width: 100%;
          border: 2px solid #e5e7eb;
          border-radius: 10px;
          padding: 10px 14px;
          font-size: 0.85rem;
          resize: none;
          transition: border-color 0.2s ease;
        }

        .reply-input:focus {
          outline: none;
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }

        .reply-actions {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }

        .btn-reply, .btn-save {
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          border: none;
          color: white;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 0.8rem;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .btn-reply:hover, .btn-save:hover {
          background: linear-gradient(135deg, #5b5bd6 0%, #7c3aed 100%);
          transform: translateY(-1px);
        }

        .btn-cancel {
          background: none;
          border: 2px solid #d1d5db;
          color: #6b7280;
          padding: 6px 16px;
          border-radius: 8px;
          font-size: 0.8rem;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        .btn-cancel:hover {
          border-color: #9ca3af;
          color: #374151;
        }

        .comment-edit-form {
          margin: 6px 0;
        }

        .comment-edit-input {
          width: 100%;
          border: 2px solid #e5e7eb;
          border-radius: 10px;
          padding: 12px 16px;
          font-size: 0.9rem;
          resize: vertical;
          min-height: 80px;
          transition: border-color 0.2s ease;
        }

        .comment-edit-input:focus {
          outline: none;
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }

        .comment-edit-actions {
          margin-top: 10px;
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }

        .no-comments {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          color: #9ca3af;
          font-size: 0.9rem;
          gap: 12px;
        }

        /* Attachment Styles */
        .attachment-section {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 8px;
          flex-wrap: wrap;
        }

        .attachment-btn {
          background: #f3f4f6;
          border: 1px solid #e5e7eb;
          color: #6b7280;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 0.8rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s ease;
        }

        .attachment-btn:hover {
          background: #e5e7eb;
          color: #374151;
        }

        .selected-attachment {
          position: relative;
          display: inline-block;
        }

        .attachment-preview {
          width: 60px;
          height: 60px;
          object-fit: cover;
          border-radius: 6px;
          border: 2px solid #e5e7eb;
        }

        .remove-attachment {
          position: absolute;
          top: -6px;
          right: -6px;
          background: #ef4444;
          color: white;
          border: none;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 10px;
        }

        .comment-attachment {
          margin-top: 8px;
        }

        .attachment-image {
          max-width: 300px;
          max-height: 200px;
          object-fit: cover;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          cursor: pointer;
          transition: transform 0.2s ease;
        }

        .attachment-image:hover {
          transform: scale(1.02);
        }

        .spinner-inline {
          width: 16px;
          height: 16px;
          border: 2px solid #ffffff;
          border-top: 2px solid transparent;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Image Selection Modal Styles */
        .upload-section {
          margin-bottom: 20px;
          text-align: center;
        }

        .upload-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          color: white;
          padding: 12px 24px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s ease;
          text-decoration: none;
        }

        .upload-btn:hover:not(.uploading) {
          background: linear-gradient(135deg, #5b5bd6 0%, #7c3aed 100%);
          transform: translateY(-1px);
        }

        .upload-btn.uploading {
          background: #d1d5db;
          cursor: not-allowed;
        }

        .image-gallery {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: 15px;
          max-height: 400px;
          overflow-y: auto;
        }

        .image-item {
          position: relative;
          aspect-ratio: 1;
          border-radius: 8px;
          overflow: hidden;
          border: 2px solid transparent;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .image-item:hover {
          border-color: #6366f1;
          transform: scale(1.02);
        }

        .image-item.selected {
          border-color: #6366f1;
          box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.2);
        }

        .image-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .image-selected-indicator {
          position: absolute;
          top: 8px;
          right: 8px;
          background: #6366f1;
          color: white;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* NEW: Image delete button styles */
        .image-delete-btn {
          position: absolute;
          top: 8px;
          left: 8px;
          background: rgba(220, 38, 38, 0.9);
          color: white;
          border: none;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 10px;
          opacity: 0;
          transition: all 0.2s ease;
          z-index: 10;
        }

        .image-item:hover .image-delete-btn {
          opacity: 1;
        }

        .image-delete-btn:hover {
          background: rgba(220, 38, 38, 1);
          transform: scale(1.1);
        }

        .image-delete-btn:disabled {
          background: rgba(156, 163, 175, 0.9);
          cursor: not-allowed;
          transform: none;
        }

        /* FIXED: Image overlay for filename display */
        .image-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(transparent, rgba(0,0,0,0.7));
          color: white;
          padding: 8px 6px 4px;
          font-size: 0.7rem;
          opacity: 0;
          transition: opacity 0.2s ease;
        }

        .image-item:hover .image-overlay {
          opacity: 1;
        }

        .image-filename {
          display: block;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-weight: 500;
        }

        /* FIXED: Error placeholder styles */
        .image-error-placeholder {
          width: 100%;
          height: 100%;
          min-height: 150px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .no-images {
          text-align: center;
          padding: 40px 20px;
          color: #9ca3af;
          grid-column: 1 / -1;
        }

        .no-images p {
          margin: 10px 0 0 0;
        }

        .comments-section-error {
          padding: 20px;
        }

        /* Custom Toast Styles */
        :global(.custom-toast) {
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          font-family: inherit;
        }

        :global(.custom-toast-body) {
          font-size: 0.9rem;
          line-height: 1.4;
        }

        :global(.custom-toast-progress) {
          height: 3px;
        }

        /* Swal2 Custom Styles */
        :global(.swal2-popup) {
          border-radius: 12px;
          font-family: inherit;
        }

        :global(.swal2-title) {
          font-size: 1.5rem;
          font-weight: 600;
        }

        :global(.swal2-content) {
          font-size: 1rem;
          line-height: 1.5;
        }

        :global(.swal2-confirm) {
          border-radius: 8px;
          padding: 10px 24px;
          font-weight: 600;
        }

        :global(.swal2-cancel) {
          border-radius: 8px;
          padding: 10px 24px;
          font-weight: 600;
        }
      `}</style>
    </>
  );
};

export default CommentsSection;
